import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, db } from './firebase.js';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import {
  initializeSubscription,
  cleanupSubscription,
  handleCheckoutSuccess,
  handleCheckoutCanceled
} from './subscription.js';

/**
 * Authentication module for BudgetApp
 */

// Global auth state
let currentUser = null;
let isSigningIn = false;
let authInitialized = false;
let loadStartTime = Date.now();

// DOM elements
const appLoadingContainer = document.getElementById('app-loading-container');
const authContainer = document.getElementById('auth-container');
const mainContainer = document.getElementById('main-container');
const authLoading = document.getElementById('auth-loading');
const authError = document.getElementById('auth-error');
const authSuccess = document.getElementById('auth-success');
const signinForm = document.getElementById('signin-form');
const signupForm = document.getElementById('signup-form');
const authToggleLink = document.getElementById('auth-toggle-link');
const authToggleText = document.getElementById('auth-toggle-text');

// Send password reset email
export async function sendPasswordReset(email) {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    return { success: false, error: getErrorMessage(error.code) };
  }
}

// Show password reset form in the login box
export function showPasswordResetModal() {
  // Get form elements fresh (in case they weren't available during initialization)
  const currentSigninForm = document.getElementById('signin-form');
  const currentSignupForm = document.getElementById('signup-form');
  
  // Hide the sign-in and sign-up forms
  if (currentSigninForm) currentSigninForm.classList.add('hidden');
  if (currentSignupForm) currentSignupForm.classList.add('hidden');
  
  // Show or create password reset form
  let resetForm = document.getElementById('password-reset-form-inline');
  
  if (!resetForm) {
    // Create the password reset form if it doesn't exist
    resetForm = createPasswordResetForm();
  }
  
  if (resetForm) {
    resetForm.classList.remove('hidden');
    
    // Focus on the email input
    setTimeout(() => {
      const emailInput = document.getElementById('reset-email-inline');
      if (emailInput) {
        emailInput.focus();
      }
    }, 100);
  }
}

// Create inline password reset form
function createPasswordResetForm() {
  // Try multiple possible container selectors
  let authContainer = document.querySelector('.auth-container .auth-content');
  if (!authContainer) {
    authContainer = document.querySelector('.auth-container');
  }
  if (!authContainer) {
    authContainer = document.getElementById('auth-container');
  }
  
  if (!authContainer) {
    return null;
  }
  
  const resetForm = document.createElement('form');
  resetForm.id = 'password-reset-form-inline';
  resetForm.className = 'auth-form';
  
  resetForm.innerHTML = `
    <h2 style="text-align: center; margin-bottom: 24px; color: var(--text-dark);">
      🔑 Reset Password
    </h2>
    <p style="text-align: center; margin-bottom: 24px; color: var(--text-light); font-size: 0.9em;">
      Enter your email address and we'll send you a link to reset your password.
    </p>
    
    <div class="form-group">
      <label for="reset-email-inline">Email Address</label>
      <input type="email" id="reset-email-inline" name="email" required placeholder="Enter your email">
    </div>
    
    <div class="error" id="password-reset-error-inline" style="display: none;"></div>
    <div class="success" id="password-reset-success-inline" style="display: none;"></div>
    
    <button type="submit" class="btn primary full-width" style="margin-bottom: 16px;">
      Send Reset Link
    </button>
    
    <div style="text-align: center;">
      <a href="#" id="back-to-login-link" style="color: var(--primary-color); text-decoration: none; font-size: 0.9em;">
        ← Back to Sign In
      </a>
    </div>
  `;
  
  // Add event listeners
  resetForm.addEventListener('submit', handlePasswordResetInlineSubmit);
  
  const backLink = resetForm.querySelector('#back-to-login-link');
  if (backLink) {
    backLink.addEventListener('click', (e) => {
      e.preventDefault();
      hidePasswordResetForm();
    });
  }
  
  // Insert the form into the auth container
  const signupForm = document.getElementById('signup-form');
  if (signupForm && signupForm.parentNode) {
    signupForm.parentNode.insertBefore(resetForm, signupForm.nextSibling);
  } else {
    // Fallback: append directly to auth container
    authContainer.appendChild(resetForm);
  }
  
  return resetForm;
}

// Handle inline password reset form submission
async function handlePasswordResetInlineSubmit(e) {
  e.preventDefault();
  
  const emailInput = document.getElementById('reset-email-inline');
  const errorEl = document.getElementById('password-reset-error-inline');
  const successEl = document.getElementById('password-reset-success-inline');
  
  if (!emailInput || !errorEl || !successEl) return;
  
  const email = emailInput.value.trim();
  
  // Clear previous messages
  errorEl.style.display = 'none';
  successEl.style.display = 'none';
  
  if (!email) {
    errorEl.textContent = 'Please enter your email address.';
    errorEl.style.display = 'block';
    return;
  }
  
  
  try {
    const result = await sendPasswordReset(email);
    
    if (result.success) {
      successEl.textContent = 'Password reset link sent! Check your email for instructions.';
      successEl.style.display = 'block';
      emailInput.value = ''; // Clear the input
    } else {
      errorEl.textContent = result.error;
      errorEl.style.display = 'block';
    }
  } catch (error) {
    console.error('Password reset error:', error);
    errorEl.textContent = 'Failed to send password reset email. Please try again.';
    errorEl.style.display = 'block';
  }
}

// Hide password reset form and show login form
function hidePasswordResetForm() {
  const resetForm = document.getElementById('password-reset-form-inline');
  if (resetForm) {
    resetForm.classList.add('hidden');
  }
  
  // Get form elements fresh
  const currentSigninForm = document.getElementById('signin-form');
  const currentSignupForm = document.getElementById('signup-form');
  
  // Show sign-in form
  if (currentSigninForm) {
    currentSigninForm.classList.remove('hidden');
  }
  
  // Hide sign-up form
  if (currentSignupForm) {
    currentSignupForm.classList.add('hidden');
  }
  
  // Update toggle link
  const authToggleText = document.getElementById('auth-toggle-text');
  if (authToggleText) {
    authToggleText.innerHTML = 'Don\'t have an account? <a id="auth-toggle-link">Sign up</a>';
  }
}

// Close password reset modal
export function closePasswordResetModal() {
  const modal = document.getElementById('password-reset-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

// Handle password reset form submission
export async function handlePasswordResetSubmit() {
  const emailInput = document.getElementById('reset-email');
  const errorEl = document.getElementById('password-reset-error');
  const successEl = document.getElementById('password-reset-success');
  
  if (!emailInput || !errorEl || !successEl) return;
  
  const email = emailInput.value.trim();
  
  if (!email) {
    errorEl.textContent = 'Please enter your email address.';
    errorEl.style.display = 'block';
    successEl.style.display = 'none';
    return;
  }
  
  const result = await sendPasswordReset(email);
  
  if (result.success) {
    successEl.textContent = 'Password reset link sent! Check your email.';
    successEl.style.display = 'block';
    errorEl.style.display = 'none';
    // Close modal after 2 seconds
    setTimeout(() => {
      closePasswordResetModal();
    }, 2000);
  } else {
    errorEl.textContent = result.error;
    errorEl.style.display = 'block';
    successEl.style.display = 'none';
  }
}

// Sign in with email and password
export async function signIn(email, password) {
  if (isSigningIn) return;
  
  isSigningIn = true;
  try {
    await signInWithEmailAndPassword(auth, email, password);
    showSuccess('Successfully signed in!');
  } catch (error) {
    console.error('Sign-in error:', error);
    showError(getErrorMessage(error.code));
  } finally {
    isSigningIn = false;
  }
}

// Sign up with email and password
export async function signUp(email, password) {
  if (isSigningIn) return;

  isSigningIn = true;
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await createUserData(userCredential.user);
    isSigningIn = false;
  } catch (error) {
    console.error('Sign-up error:', error);
    showError(getErrorMessage(error.code));
    isSigningIn = false;
  }
}

// Sign in with Google
export async function signInWithGoogle() {
  if (isSigningIn) return;

  isSigningIn = true;
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

    if (isNewUser) {
      await createUserData(result.user);
    } else {
      showSuccess('Successfully signed in with Google!');
    }
    isSigningIn = false;
  } catch (error) {
    console.error('Google sign-in error:', error);
    showError(getErrorMessage(error.code));
    isSigningIn = false;
  }
}

// Sign out
export async function signOut() {
  try {
    await firebaseSignOut(auth);
    showSuccess('Successfully signed out!');
  } catch (error) {
    console.error('Sign-out error:', error);
    showError('Error signing out. Please try again.');
  }
}

// Hide initial loading screen with minimum display time
async function hideAppLoading() {
  if (!appLoadingContainer) return;
  
  const minLoadTime = 800; // Minimum 800ms to prevent flash
  const elapsed = Date.now() - loadStartTime;
  
  if (elapsed < minLoadTime) {
    await new Promise(resolve => setTimeout(resolve, minLoadTime - elapsed));
  }
  
  appLoadingContainer.style.display = 'none';
}

// Display authentication UI
export async function showAuth() {
  await hideAppLoading();
  if (authContainer) authContainer.classList.remove('hidden');
  if (mainContainer) mainContainer.classList.add('hidden');
  if (authLoading) authLoading.style.display = 'none';
}

// Display main application
export async function showApp() {
  await hideAppLoading();
  if (authContainer) authContainer.classList.add('hidden');
  if (mainContainer) mainContainer.classList.remove('hidden');
}

// Toggle between sign-in and sign-up forms
export function toggleAuthForm() {
  // Get form elements fresh
  const currentSigninForm = document.getElementById('signin-form');
  const currentSignupForm = document.getElementById('signup-form');
  const currentAuthToggleText = document.getElementById('auth-toggle-text');
  const resetForm = document.getElementById('password-reset-form-inline');
  
  if (!currentSigninForm || !currentSignupForm || !currentAuthToggleText) return;
  
  // Hide password reset form if it's visible
  if (resetForm && !resetForm.classList.contains('hidden')) {
    resetForm.classList.add('hidden');
  }
  
  const isSignupVisible = !currentSignupForm.classList.contains('hidden');
  
  if (isSignupVisible) {
    // Switch to sign-in
    currentSignupForm.classList.add('hidden');
    currentSigninForm.classList.remove('hidden');
    currentAuthToggleText.innerHTML = 'Don\'t have an account? <a id="auth-toggle-link">Sign up</a>';
  } else {
    // Switch to sign-up
    currentSigninForm.classList.add('hidden');
    currentSignupForm.classList.remove('hidden');
    currentAuthToggleText.innerHTML = 'Already have an account? <a id="auth-toggle-link">Sign in</a>';
  }
}

// Show error message
export function showError(message) {
  if (!authError) return;
  
  authError.textContent = message;
  authError.style.display = 'block';
  if (authSuccess) authSuccess.style.display = 'none';
  
  // Auto-hide after 5 seconds
  setTimeout(() => {
    if (authError) authError.style.display = 'none';
  }, 5000);
}

// Show success message
export function showSuccess(message) {
  if (!authSuccess) return;
  
  authSuccess.textContent = message;
  authSuccess.style.display = 'block';
  if (authError) authError.style.display = 'none';
  
  // Auto-hide after 3 seconds
  setTimeout(() => {
    if (authSuccess) authSuccess.style.display = 'none';
  }, 3000);
}

// Convert Firebase error codes to user-friendly messages
export function getErrorMessage(errorCode) {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/missing-email':
      return 'Please enter your email address.';
    default:
      return 'An error occurred. Please try again.';
  }
}

// Get current user
export function getCurrentUser() {
  return currentUser;
}

// Create initial user data with membership info
// New users get a 30-day free trial, no payment required upfront
async function createUserData(user) {
  const now = new Date();
  const trialExpiry = new Date(now);
  trialExpiry.setDate(trialExpiry.getDate() + 30);

  const userData = {
    email: user.email,
    membership: {
      status: 'trial',
      signupDate: now.toISOString(),
      trialExpiry: trialExpiry
    }
  };

  try {
    await setDoc(doc(db, 'users', user.uid), userData, { merge: true });
    console.log('User membership data created successfully');
  } catch (error) {
    console.error('Error creating user data:', error);
  }
}

// Set up authentication state observer
export function initializeAuth(onUserChange) {
  return onAuthStateChanged(auth, async (user) => {
    // Clear budget data when switching users or signing out
    if (currentUser && (!user || currentUser.uid !== user.uid)) {
      console.log('User changed, clearing previous user data');
      // Import and call clearBudgetData when switching users
      try {
        const { clearBudgetData } = await import('./budget.js');
        await clearBudgetData();
      } catch (error) {
        console.error('Error clearing budget data:', error);
      }

      // Cleanup subscription listener
      cleanupSubscription();
    }
    
    currentUser = user;
    authInitialized = true;
    
    if (user) {
      console.log('User signed in:', user.email);
      await showApp();

      // Initialize subscription management and trial expiry checking
      initializeSubscription(user);

      // Handle Stripe checkout return URLs
      handleCheckoutSuccess();
      handleCheckoutCanceled();

      // Update current account display
      updateCurrentAccountDisplay();

      // Call the callback if provided (loads user data including linked account detection)
      if (onUserChange && typeof onUserChange === 'function') {
        await onUserChange(user);
      }

      // Track last login AFTER data load to avoid partial-document cache pollution.
      // setDoc(merge:true) on an uncached document writes only the specified fields to
      // the local Firestore cache, which would cause loadUserData to miss linkedAccount.
      try {
        await setDoc(doc(db, 'users', user.uid), {
          lastLoginAt: new Date().toISOString()
        }, { merge: true });
      } catch (e) {
        console.error('Error updating lastLoginAt:', e);
      }
    } else {
      console.log('User signed out');
      currentUser = null;

      // Cleanup subscription listener
      cleanupSubscription();

      await showAuth();
    }
  });
}

// Update current account display
function updateCurrentAccountDisplay() {
  const currentAccountEl = document.getElementById('current-account');
  if (currentAccountEl && currentUser) {
    currentAccountEl.textContent = currentUser.email;
  }
  
  // Update membership display
  updateMembershipDisplay();
}

// Get user membership data
export async function getUserMembershipData() {
  if (!currentUser) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.membership || null;
    }
  } catch (error) {
    console.error('Error fetching membership data:', error);
  }
  return null;
}

// Update membership display in UI
export async function updateMembershipDisplay() {
  // Use the subscription module's active listener data so linked users
  // inherit the owner's membership status rather than their own.
  const { getMembershipData } = await import('./subscription.js');
  const membershipData = getMembershipData();

  if (!membershipData || !membershipData.status) return;
  
  // Update main membership badge
  const membershipBadge = document.getElementById('membership-badge');
  const membershipBadgeSmall = document.getElementById('membership-badge-small');
  const membershipDetails = document.getElementById('membership-details');
  const memberSince = document.getElementById('member-since');
  
  const status = membershipData.status || 'trial';
  const trialExpiry = membershipData.trialExpiry
    ? (typeof membershipData.trialExpiry.toDate === 'function' ? membershipData.trialExpiry.toDate() : new Date(membershipData.trialExpiry))
    : null;
  
  // Update badge text and style
  const badgeText = status === 'trial' ? 'Free Trial' : 
                   status === 'premium' ? 'Premium' : 
                   status === 'expired' ? 'Expired' : 'Free Trial';
  
  if (membershipBadge) {
    membershipBadge.textContent = badgeText;
    membershipBadge.className = `membership-badge ${status}`;
  }
  
  if (membershipBadgeSmall) {
    membershipBadgeSmall.textContent = badgeText;
    membershipBadgeSmall.className = `membership-badge-small ${status}`;
  }
  
  // Update details
  if (membershipDetails) {
    if (status === 'trial' && trialExpiry) {
      const daysLeft = Math.ceil((trialExpiry - new Date()) / (1000 * 60 * 60 * 24));
      membershipDetails.textContent = daysLeft > 0
        ? `Trial expires in ${daysLeft} days (${trialExpiry.toLocaleDateString()})`
        : 'Trial expired';
    } else if (status === 'premium') {
      membershipDetails.textContent = 'Active subscription';
    } else {
      membershipDetails.textContent = 'Trial expired';
    }
  }
}

// Initialize authentication event listeners
export function setupAuthEventListeners() {
  // Sign-in form
  if (signinForm) {
    signinForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = document.getElementById('signin-email')?.value;
      const password = document.getElementById('signin-password')?.value;
      if (email && password) {
        await signIn(email, password);
      }
    });
  }
  
  // Sign-up form
  if (signupForm) {
    signupForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = document.getElementById('signup-email')?.value;
      const password = document.getElementById('signup-password')?.value;
      const confirmPassword = document.getElementById('signup-confirm')?.value;
      
      if (password !== confirmPassword) {
        showError('Passwords do not match.');
        return;
      }
      
      if (email && password) {
        await signUp(email, password);
      }
    });
  }
  
  // Google sign-in buttons
  const googleSigninBtn = document.getElementById('google-signin-btn');
  const googleSignupBtn = document.getElementById('google-signup-btn');
  
  if (googleSigninBtn) {
    googleSigninBtn.addEventListener('click', signInWithGoogle);
  }
  
  if (googleSignupBtn) {
    googleSignupBtn.addEventListener('click', signInWithGoogle);
  }
  
  // Auth form toggle - use event delegation to handle dynamic content
  const authToggleText = document.getElementById('auth-toggle-text');
  if (authToggleText) {
    authToggleText.addEventListener('click', (e) => {
      if (e.target.id === 'auth-toggle-link') {
        e.preventDefault();
        toggleAuthForm();
      }
    });
  }
  
  // Forgot password link - use event delegation to handle potential dynamic updates
  document.addEventListener('click', (e) => {
    if (e.target.id === 'forgot-password-link') {
      e.preventDefault();
      showPasswordResetModal();
    }
  });
  
  // Password toggle functionality
  setupPasswordToggle();
}

// Setup password visibility toggle functionality
function setupPasswordToggle() {
  // Use event delegation to handle all password toggle buttons
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('password-toggle') || e.target.closest('.password-toggle')) {
      const toggleButton = e.target.classList.contains('password-toggle') ? e.target : e.target.closest('.password-toggle');
      const targetId = toggleButton.getAttribute('data-target');
      const passwordInput = document.getElementById(targetId);
      
      if (passwordInput) {
        const isVisible = passwordInput.type === 'text';
        
        if (isVisible) {
          // Hide password
          passwordInput.type = 'password';
          toggleButton.setAttribute('data-visible', 'false');
          toggleButton.title = 'Show Password';
          const icon = toggleButton.querySelector('.password-toggle-icon');
          if (icon) icon.textContent = '👁️';
        } else {
          // Show password
          passwordInput.type = 'text';
          toggleButton.setAttribute('data-visible', 'true');
          toggleButton.title = 'Hide Password';
          const icon = toggleButton.querySelector('.password-toggle-icon');
          if (icon) icon.textContent = '🙈';
        }
      }
    }
  });
}

// Export auth state for global access
export { currentUser, isSigningIn };